import { useState } from "react";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";

// EmailJS Configuration
const EMAILJS_CONFIG = {
  SERVICE_ID: "service_a8t9sbt",
  TEMPLATE_ID: "template_kkd9iri",
  PUBLIC_KEY: "PinX8mgdGG_GoQx52",
  TO_NAME: "Decodia",
  TO_EMAIL: "info@decodia.in"
};

const formatFormDataForEmail = (formData) => {
  return `
    📋 New Brochure Access Request
    
    ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    
    👤 Customer Details:
    • Name: ${formData.name || "Not provided"}
    • Email: ${formData.email || "Not provided"}
    • Phone: ${formData.phone || "Not provided"}
    
    📄 Request Type: Brochure Access (Decodia Brochure 2026)
    
    🌐 Source: Website Popup Form
    
    ⏰ Request Time: ${new Date().toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })}
    
    ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    
    📌 Next Steps:
    1. Customer will receive brochure access
    2. Follow up email to be sent
    3. Add to newsletter list (if consented)
    
    This is an automated request from the Decodia website.
  `;
};

const sendBrochureEmail = async (formData) => {
  try {
    const templateParams = {
      to_name: EMAILJS_CONFIG.TO_NAME,
      to_email: EMAILJS_CONFIG.TO_EMAIL,
      from_name: formData.name || "Customer",
      from_email: formData.email || "Not provided",
      from_phone: formData.phone || "Not provided",
      subject: `New Brochure Request from ${formData.name}`,
      message: formatFormDataForEmail(formData),
      date: new Date().toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }),
      site: "DECODIA Website - Brochure Popup",
    };

    const response = await emailjs.send(
      EMAILJS_CONFIG.SERVICE_ID,
      EMAILJS_CONFIG.TEMPLATE_ID,
      templateParams,
      EMAILJS_CONFIG.PUBLIC_KEY
    );

    return { success: true, response };
    
  } catch (error) {
    let errorMessage = "Email service error";
    if (error.text) errorMessage = error.text;
    else if (error.message) errorMessage = error.message;
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};

function PopupForm({ onFormSubmit }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const validateForm = () => {
    let isValid = true;
    const newErrors = { name: "", email: "", phone: "" };

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ""))) {
      newErrors.phone = "Phone number must be exactly 10 digits";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setEmailError("");
    
    if (validateForm()) {
      setIsSubmitting(true);
      
      try {
        console.log("Sending email with data:", formData);
        
        const emailResult = await sendBrochureEmail(formData);
        
        if (emailResult.success) {
          console.log("Email sent successfully:", emailResult.response);
          
          const allSubmissions = JSON.parse(localStorage.getItem('brochureRequests') || '[]');
          allSubmissions.push({
            ...formData,
            timestamp: new Date().toISOString(),
          });
          localStorage.setItem('brochureRequests', JSON.stringify(allSubmissions));
          
          // Store form data for thank you page
          localStorage.setItem('lastFormData', JSON.stringify(formData));
          
          // Close the popup and navigate to thank you page
          setIsSubmitting(false);
          onFormSubmit(false); // Close popup
          navigate('/thank-you', { state: { formData } });
          
        } else {
          console.error("Email failed:", emailResult.error);
          setEmailError("Failed to send request. Please try again or contact us directly.");
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error("Error in form submission:", error);
        setEmailError("An unexpected error occurred. Please try again.");
        setIsSubmitting(false);
      }
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.8)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
        backdropFilter: "blur(5px)",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          padding: "35px",
          borderRadius: "16px",
          maxWidth: "450px",
          width: "90%",
          boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          animation: "slideIn 0.3s ease-out",
          position: "relative",
        }}
      >
        {/* Close button */}
        <button
          onClick={() => onFormSubmit(false)}
          style={{
            position: "absolute",
            top: "15px",
            right: "15px",
            background: "none",
            border: "none",
            fontSize: "24px",
            cursor: "pointer",
            color: "#7f8c8d",
            padding: "5px 10px",
            transition: "color 0.3s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "#e74c3c";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "#7f8c8d";
          }}
        >
          ✕
        </button>

        <>
          <h2
            style={{
              marginBottom: "10px",
              color: "#2c3e50",
              textAlign: "center",
              fontSize: "28px",
            }}
          >
            📄 Access Decodia Brochure 2026 
          </h2>
          <p
            style={{
              marginBottom: "25px",
              color: "#7f8c8d",
              textAlign: "center",
              fontSize: "14px",
            }}
          >
            Please fill in your details to view the brochure
          </p>

          {emailError && (
            <div
              style={{
                backgroundColor: "#fee",
                color: "#c0392b",
                padding: "10px",
                borderRadius: "8px",
                marginBottom: "20px",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              ⚠️ {emailError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#34495e",
                  fontSize: "14px",
                }}
              >
                Full Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${errors.name ? "#e74c3c" : "#dfe6e9"}`,
                  borderRadius: "8px",
                  fontSize: "15px",
                  outline: "none",
                  transition: "all 0.3s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3498db";
                }}
                onBlur={(e) => {
                  if (!errors.name) e.target.style.borderColor = "#dfe6e9";
                }}
              />
              {errors.name && (
                <p style={{ color: "#e74c3c", fontSize: "12px", marginTop: "5px" }}>
                  ⚠️ {errors.name}
                </p>
              )}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#34495e",
                  fontSize: "14px",
                }}
              >
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email address"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${errors.email ? "#e74c3c" : "#dfe6e9"}`,
                  borderRadius: "8px",
                  fontSize: "15px",
                  outline: "none",
                  transition: "all 0.3s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3498db";
                }}
                onBlur={(e) => {
                  if (!errors.email) e.target.style.borderColor = "#dfe6e9";
                }}
              />
              {errors.email && (
                <p style={{ color: "#e74c3c", fontSize: "12px", marginTop: "5px" }}>
                  ⚠️ {errors.email}
                </p>
              )}
            </div>

            <div style={{ marginBottom: "25px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#34495e",
                  fontSize: "14px",
                }}
              >
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter 10-digit mobile number"
                style={{
                  width: "100%",
                  padding: "12px",
                  border: `2px solid ${errors.phone ? "#e74c3c" : "#dfe6e9"}`,
                  borderRadius: "8px",
                  fontSize: "15px",
                  outline: "none",
                  transition: "all 0.3s",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#3498db";
                }}
                onBlur={(e) => {
                  if (!errors.phone) e.target.style.borderColor = "#dfe6e9";
                }}
              />
              {errors.phone && (
                <p style={{ color: "#e74c3c", fontSize: "12px", marginTop: "5px" }}>
                  ⚠️ {errors.phone}
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: "100%",
                padding: "14px",
                backgroundColor: isSubmitting ? "#95a5a6" : "#3498db",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                transition: "all 0.3s",
              }}
              onMouseEnter={(e) => {
                if (!isSubmitting) e.currentTarget.style.backgroundColor = "#2980b9";
              }}
              onMouseLeave={(e) => {
                if (!isSubmitting) e.currentTarget.style.backgroundColor = "#3498db";
              }}
            >
              {isSubmitting ? "Sending..." : "Submit & Continue"}
            </button>
          </form>
        </>
      </div>

      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateY(-50px);
              opacity: 0;
            }
            to {
              transform: translateY(0);
              opacity: 1;
            }
          },
        `}
      </style>
    </div>
  );
}
 
export default PopupForm;